import type { EmployeePatch, EmployerSnapshot, NewEmployeeInput } from '@klukka/client';
import {
  AGREEMENTS,
  FUNDS,
  fmt,
  LANGS,
  seniorityYears,
  unionRatesFor,
  type AdminLang,
  type Employee,
} from '@klukka/core';
import { Ic } from '@klukka/ui';
import { useEffect, useState } from 'react';
import type { EmployeeCalc } from '../derive';
import { Fld, Panel, Select, type Tr } from '../widgets';

/**
 * The employee grid.
 *
 * Each card leads with the rates band — the numbers an employer checks first —
 * and buries the rest in accordions so a company with twenty people can still
 * scan the page. Text fields commit on blur so a real API sees one write per
 * edit, not one per keystroke.
 */
export function EmployeesTab({ snap, all, tr, lang, actions }: {
  snap: EmployerSnapshot;
  all: EmployeeCalc[];
  tr: Tr;
  lang: AdminLang;
  actions: {
    create: (input: NewEmployeeInput) => void;
    update: (empId: string, patch: EmployeePatch) => void;
    deactivate: (empId: string) => void;
    reactivate: (empId: string) => void;
    remove: (empId: string) => void;
    setGrant: (empId: string, grant: { from: string; to: string; until: string } | null) => void;
  };
}) {
  const [adding, setAdding] = useState(false);

  return (
    <div>
      <Panel style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span className="dimtx" style={{ fontSize: 13 }}>{tr('selfService')}</span>
        <button className="btn-sm ok" onClick={() => setAdding(true)}><Ic.plus size={14} /> {tr('addEmp')}</button>
      </Panel>

      {adding ? (
        <NewEmployeeForm
          tr={tr}
          onCancel={() => setAdding(false)}
          onCreate={(input) => { actions.create(input); setAdding(false); }}
        />
      ) : null}

      {snap.employees.map((emp) =>
        emp.inactive ? (
          <Panel key={emp.id} style={{ opacity: .65, background: '#f2f6f5' }}>
            <div className="p-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
              <span>
                {emp.name} <span className="dimtx">· {emp.role || '—'} · kt. {emp.kennitala || '—'}</span>
                <span className="chip" style={{ borderColor: 'var(--ink3)', color: 'var(--ink3)', marginLeft: 8 }}>{tr('inactiveLbl')}</span>
              </span>
              <button className="btn-sm ok" onClick={() => actions.reactivate(emp.id)}>{tr('reactivate')}</button>
            </div>
          </Panel>
        ) : (
          <EmployeeCard
            key={emp.id}
            emp={emp}
            calc={all.find((x) => x.emp.id === emp.id)?.calc ?? null}
            snap={snap}
            tr={tr}
            lang={lang}
            actions={actions}
          />
        ),
      )}

      <div className="dimtx">{tr('save')}</div>
    </div>
  );
}

function NewEmployeeForm({ tr, onCreate, onCancel }: {
  tr: Tr;
  onCreate: (input: NewEmployeeInput) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [kennitala, setKennitala] = useState('');
  const [role, setRole] = useState('');
  const [wage, setWage] = useState(600000);

  return (
    <Panel title={tr('addEmp')}>
      <div className="s-row" style={{ alignItems: 'flex-end' }}>
        <Fld label={tr('nameLbl')} value={name} onChange={setName} type="text" width="220px" />
        <Fld label={tr('ktLbl')} value={kennitala} onChange={setKennitala} type="text" width="150px" placeholder="000000-0000" />
        <Fld label={tr('roleLbl')} value={role} onChange={setRole} type="text" width="160px" />
        <Fld label={tr('monthlyWage')} value={wage} onChange={setWage} suffix="kr." width="160px" />
        <button className="btn-sm" style={{ height: 36 }} onClick={onCancel}>{tr('cancel')}</button>
        <button
          className="btn-sm ok"
          style={{ height: 36 }}
          disabled={!name.trim() || !kennitala.trim()}
          onClick={() => onCreate({ name: name.trim(), kennitala: kennitala.trim(), role: role.trim(), monthlyWage: wage })}
        >
          <Ic.plus size={14} /> {tr('addEmp')}
        </button>
      </div>
      <div className="dimtx" style={{ fontSize: 12, marginTop: 10 }}>{tr('selfService')}</div>
    </Panel>
  );
}

function EmployeeCard({ emp, calc, snap, tr, lang, actions }: {
  emp: Employee;
  calc: EmployeeCalc['calc'] | null;
  snap: EmployerSnapshot;
  tr: Tr;
  lang: AdminLang;
  actions: Parameters<typeof EmployeesTab>[0]['actions'];
}) {
  const settings = snap.company.settings;
  const grant = snap.editGrants[emp.id] ?? null;
  const update = (patch: EmployeePatch) => actions.update(emp.id, patch);

  // An employee with history is archived, never deleted — payroll records
  // outlive employment and the audit trail has to stay intact.
  const hasRecords =
    Object.keys(emp.worklog).length > 0 ||
    snap.timesheets.some((t) => t.empId === emp.id) ||
    snap.requests.some((r) => r.empId === emp.id) ||
    snap.editLog.some((l) => l.empId === emp.id);

  const seniority = (() => {
    const y = seniorityYears(emp, settings);
    const years = Math.floor(y);
    const months = Math.floor((y - years) * 12);
    return lang === 'is'
      ? `${tr('seniority')} ${years} ár ${months} mán.`
      : `${tr('seniority')} ${years}y ${months}m`;
  })();

  const unionRates = unionRatesFor(emp, settings);
  const companyAgreement = AGREEMENTS.find((a) => a.id === settings.agreementId);

  return (
    <Panel>
      <div className="p-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span>{emp.name} <span className="dimtx">· {emp.role || '—'} · kt. {emp.kennitala || '—'}</span></span>
        {hasRecords ? (
          <button
            className="btn-sm"
            title={tr('cannotDelete')}
            onClick={() => { if (window.confirm(`${tr('deactivate')}: ${emp.name}?\n\n${tr('cannotDelete')}`)) actions.deactivate(emp.id); }}
          >
            <Ic.lock size={13} /> {tr('deactivate')}
          </button>
        ) : (
          <button
            className="btn-sm no"
            onClick={() => { if (window.confirm(`${tr('delEmp')}: ${emp.name}?`)) actions.remove(emp.id); }}
          >
            {tr('delEmp')}
          </button>
        )}
      </div>

      {calc ? (
        <div className="rates-band">
          <CommitField label={tr('monthlyWage')} value={emp.monthlyWage} suffix="kr." width="160px" onCommit={(v) => update({ monthlyWage: Number(v) })} />
          <div className="rate"><span>Dagvinna</span><b>{fmt(calc.rates.dv)}</b><i>{tr('perHour')}</i></div>
          <div className="rate"><span>Yfirvinna 1</span><b>{fmt(calc.rates.yv1)}</b><i>{tr('perHour')}</i></div>
          <div className="rate"><span>Yfirvinna 2</span><b>{fmt(calc.rates.yv2)}</b><i>{tr('perHour')} · {lang === 'is' ? 'helgar/frídagar' : 'weekends/holidays'}</i></div>
          {emp.agreementId ? (
            <div className="rate">
              <span>{tr('agreement')}</span>
              <b style={{ fontSize: 13, lineHeight: '21px' }}>
                {(AGREEMENTS.find((a) => a.id === emp.agreementId)?.name ?? '').split(' — ')[0]}
              </b>
              <i>{tr('empOverride')}</i>
            </div>
          ) : null}
          <div className="rate">
            <span>Orlof</span>
            <b>{calc.orlofPctUsed.toLocaleString('de-DE')}%</b>
            <i>{seniority}</i>
          </div>
        </div>
      ) : null}

      <details className="acc-sec">
        <summary>{tr('basicsSec')}</summary>
        <div className="s-row">
          <CommitField label={tr('nameLbl')} value={emp.name} type="text" width="200px" onCommit={(v) => update({ name: String(v) })} />
          <CommitField label={tr('roleLbl')} value={emp.role} type="text" width="150px" onCommit={(v) => update({ role: String(v) })} />
          <CommitField label={tr('ktLbl')} value={emp.kennitala} type="text" width="140px" onCommit={(v) => update({ kennitala: String(v) })} />
          <CommitField label={tr('started')} value={emp.startDate} type="date" width="150px" onCommit={(v) => update({ startDate: String(v) })} />
          <CommitField label={tr('expectedHours')} value={emp.expectedHoursOverride} suffix="klst" width="210px" onCommit={(v) => update({ expectedHoursOverride: Number(v) })} />
          <Select
            label={tr('langCol')}
            width="150px"
            value={emp.lang}
            onChange={(v) => update({ lang: v as Employee['lang'] })}
            options={LANGS.map(([code, name]) => ({ value: code, label: name }))}
          />
        </div>
      </details>

      <details className="acc-sec">
        <summary>{tr('contactSec')}</summary>
        <div className="s-row">
          <CommitField label={tr('phone')} value={emp.phone} type="text" width="130px" onCommit={(v) => update({ phone: String(v) })} />
          <CommitField label="Email" value={emp.email} type="text" width="200px" onCommit={(v) => update({ email: String(v) })} />
          <CommitField label={tr('addressLbl')} value={emp.address} type="text" width="240px" onCommit={(v) => update({ address: String(v) })} />
          <CommitField label={tr('bankLbl')} value={emp.bank} type="text" width="160px" onCommit={(v) => update({ bank: String(v) })} />
        </div>
      </details>

      <details className="acc-sec">
        <summary>{tr('fundsSec')}</summary>
        <div className="s-row">
          <Select
            label={lang === 'is' ? 'Stéttarfélag' : 'Union'}
            width="180px"
            value={emp.union}
            onChange={(v) => update({ union: v })}
            options={FUNDS.unions.map((u) => ({ value: u, label: u }))}
            hint={lang === 'is'
              ? `${unionRates.member}% félagsgjald · ${unionRates.employer}% sjóðir atvinnurek.`
              : `${unionRates.member}% member fee · ${unionRates.employer}% employer funds`}
          />
          <Select
            label={tr('fund')}
            width="180px"
            value={emp.pensionFund}
            onChange={(v) => update({ pensionFund: v })}
            options={FUNDS.pension.map((p) => ({ value: p, label: p }))}
          />
          <Select
            label={tr('extraPension')}
            width="110px"
            value={emp.extraPensionPct}
            onChange={(v) => update({ extraPensionPct: Number(v) })}
            options={[{ value: 0, label: '—' }, { value: 2, label: '2%' }, { value: 4, label: '4%' }]}
          />
          <Select
            label={tr('agreement')}
            width="260px"
            value={emp.agreementId ?? ''}
            onChange={(v) => update({ agreementId: v || null })}
            options={[
              { value: '', label: `${tr('companyDefault')} — ${companyAgreement?.name ?? ''}` },
              ...AGREEMENTS.map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
          {emp.extraPensionPct ? (
            <Select
              label={tr('sereignFund')}
              width="210px"
              value={emp.extraPensionFund ?? FUNDS.sereign[0]}
              onChange={(v) => update({ extraPensionFund: v })}
              options={FUNDS.sereign.map((s) => ({ value: s, label: s }))}
            />
          ) : null}
        </div>
      </details>

      <details className="acc-sec">
        <summary>{tr('taxSec')}</summary>
        <div className="s-row">
          <CommitField label={tr('creditUtil')} value={emp.creditUtil} suffix="%" width="150px" onCommit={(v) => update({ creditUtil: Number(v) })} />
          <CommitField label={tr('priorUnusedLbl')} value={emp.priorUnusedCredit} suffix="kr." width="150px" onCommit={(v) => update({ priorUnusedCredit: Number(v) })} />
        </div>
      </details>

      <details className="acc-sec">
        <summary>
          {grant ? <Ic.unlock size={13} /> : <Ic.lock size={13} />} {tr('editAccess')}
          {grant ? (
            <span className="chip" style={{ borderColor: 'var(--ok)', color: 'var(--ok)', marginLeft: 8 }}>{tr('openRange')}</span>
          ) : null}
        </summary>
        <div style={{ padding: '6px 0 10px' }}>
          {grant ? (
            <div className="rowline" style={{ fontSize: 13 }}>
              <span>
                {tr('openRange')}: <b className="mono">{grant.from} {tr('until')} {grant.to}</b> · {tr('deadline')}: <b className="mono">{grant.until}</b>
              </span>
              <button className="btn-sm no" onClick={() => actions.setGrant(emp.id, null)}>{tr('revoke')}</button>
            </div>
          ) : (
            <GrantForm tr={tr} today={snap.serverToday} onGrant={(g) => actions.setGrant(emp.id, g)} />
          )}
        </div>
      </details>
    </Panel>
  );
}

/**
 * Text input that only reports upward once the user leaves the field — one API
 * write per edit instead of one per keystroke, without losing focus mid-typing.
 */
function CommitField({ label, value, onCommit, width, suffix, type }: {
  label: string;
  value: string | number;
  onCommit: (v: string | number) => void;
  width?: string;
  suffix?: string;
  type?: 'text' | 'number' | 'date';
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const numeric = !type || type === 'number';

  const commit = () => {
    const next = numeric ? parseFloat(draft) || 0 : draft;
    if (String(next) !== String(value)) onCommit(next);
  };

  return (
    <label className="s-fld" style={width ? { width } : undefined}>
      {label}
      <span className="s-inp-wrap">
        <input
          className="inp"
          type={type ?? 'number'}
          step="any"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        />
        {suffix ? <em>{suffix}</em> : null}
      </span>
    </label>
  );
}

function GrantForm({ tr, today, onGrant }: {
  tr: Tr;
  today: string;
  onGrant: (g: { from: string; to: string; until: string }) => void;
}) {
  const plus = (days: number) => {
    const d = new Date(`${today}T12:00`);
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(plus(-1));
  const [until, setUntil] = useState(plus(4));

  return (
    <div className="s-row" style={{ alignItems: 'flex-end' }}>
      <Fld label={tr('openRange')} value={from} onChange={setFrom} type="date" width="150px" />
      <Fld label={tr('until')} value={to} onChange={setTo} type="date" width="150px" />
      <Fld label={tr('deadline')} value={until} onChange={setUntil} type="date" width="150px" />
      <button className="btn-sm ok" style={{ height: 36 }} onClick={() => onGrant({ from, to, until })}>{tr('grant')}</button>
    </div>
  );
}
